<template>
  <v-container fluid grid-list-xl>
    <v-layout row wrap ma-0 mb-2 pa-0>
      <v-flex pt-0>
        <v-layout>
          <template>
            <h2><span class="grey--text">{{ paymentType === 'ach' ? 'ACH' : 'Card' }}</span> Transactions</h2>
            <Lightbulb medium>
              <template v-slot:message>
                <span>This screen will allow you to search for transactions using the custom search button. The Option button next to any transaction allows you to Refund, Reprint/Re-Email a receipt, recharge a customer and transactions details.</span>
              </template>
            </Lightbulb>
          </template>
        </v-layout>
      </v-flex>
    </v-layout>

    <div>
      <v-data-table
        :headers="columnsDisplayed"
        :items="rows"
        :pagination.sync="pagination"
        :loading="loading"
        :total-items="totalRows"
        :rows-per-page-items="pageCountOptions"
        item-key="id"
        class="elevation-1"
        must-sort
        no-data-text="No data found"
        no-results-text="No results found"
        @update:pagination="search"
      >
        <template slot="headers" slot-scope="props">
          <tr>
            <th
              v-for="(header) in props.headers"
              :key="header.value"
              v-show="header.visible"
              :class="['font-weight-bold text-xs-left column sortable pa-1 ma-0', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
              @click="changeSort(header.value)"
            >
              {{ header.text }}
              <v-icon small>arrow_upward</v-icon>
            </th>
            <th class="ma-0 pa-0 text-xs-right">
              <v-btn icon flat color="primary" @click="reset">
                <v-icon>fa-refresh</v-icon>
              </v-btn>
            </th>
          </tr>
        </template>

        <template slot="items" slot-scope="props">
            <tr>
            <td
              v-for="(header) in columnsDisplayed"
              :key="header.value"
              v-show="header.visible"
              class="pa-1 ma-0"
            >
              <template v-if="header.value === 'request_type'">
                <span v-if="props.item.request_type === 'ach'">Sale</span>
                <span v-else-if="props.item.request_type === 'achvoid'">Void</span>
                <span v-else-if="props.item.request_type === 'credit' || props.item.request_type === 'achcredit'">Refund</span>
                <span v-else class="text-capitalize">{{ props.item.request_type }}</span>
              </template>

              <template v-else-if="header.value === 'transaction_status'">
                <span class="text-capitalize">{{ props.item.transaction_status }}</span>
                <v-tooltip bottom content-class="tooltip-popup" max-width="300px" v-if="props.item.refunded && environment !== 'production'">
                  <template v-slot:activator="{ on }">
                    <v-chip small class="ma-0 pa-0" v-on="on">R</v-chip>
                  </template>
                  <span>Refunded</span>
                </v-tooltip>
              </template>

              <template v-else-if="header.value === 'net_amount' || header.value === 'less_fee_amount'">
                <span v-if="props.item.request_type==='credit' || props.item.request_type==='achcredit' || props.item.request_type==='void' || props.item.request_type==='achvoid'">({{ moneyNoSign((props.item.total_amount - props.item.fee_amount) / 100) }})</span>
                <span v-else>{{ moneyNoSign((props.item.total_amount - props.item.fee_amount) / 100) }}</span>
              </template>

              <template v-else-if="header.value === 'total_amount' || header.value === 'fee_amount'">
                <span v-if="props.item.request_type==='credit' || props.item.request_type==='achcredit' || props.item.request_type==='void' || props.item.request_type==='achvoid'">({{ moneyNoSign(props.item[header.value] / 100) }})</span>
                <span v-else>{{ moneyNoSign(props.item[header.value] / 100) }}</span>
              </template>

              <template v-else-if="header.value === 'is_recurring'">
                <span v-if="props.item.modify_recurring_type">{{props.item.modify_recurring_type === 'none' ? 'Stopped' : 'Yes'}}</span>
                <span v-else>{{props.item.is_recurring ? (props.item.recurring_type === 'merchant' ? 'Merchant' : (props.item.recurring_type === 'none' ? 'Stopped' : 'Yes' )) : 'No'}}</span>
              </template>

              <template v-else-if="header.value === 'card_expiration_yymm'">
                {{ expiration(props.item.card_expiration_yymm) }}
              </template>

              <template v-else-if="header.value === 'ach_is_authorized'">
                <span class="font-weight-bold red--text" v-if="!props.item.ach_is_authorized">No</span>
                <span v-else>Yes</span>
              </template>

              <template v-else-if="header.type === 'boolean'">
                <span v-if="props.item[header.value]">Yes</span>
                <span v-else>No</span>
              </template>

              <template v-else-if="header.type === 'is_hosted_payment'">
                <span v-if="props.item[header.value] === 6">Yes</span>
                <span v-else>No</span>
              </template>

              <template v-else-if="header.type === 'money'">
                {{ moneyNoSign(props.item[header.value] / 100) }}
              </template>

              <template v-else-if="header.type === 'datetime'">
                {{ datetime(props.item[header.value]) }}
              </template>

              <template v-else-if="header.type === 'date'">
                {{ dateUtc(props.item[header.value]) }}
              </template>

              <template v-else class="text-capitalize">
                {{ props.item[header.value] }}
              </template>
            </td>
            <td class="ma-0 pa-0 text-xs-right">
              <OptionsModal :transaction="props.item" :ref="'options-' + props.item.tracking_id"></OptionsModal>
            </td>
          </tr>
        </template>

        <template slot="footer" v-if="showFooter" :pagination="pagination">
          <tr id="footer">
            <td
              v-for="(header) in columnsDisplayed"
              :key="header.text"
              v-show="header.visible"
              class="pa-1 ma-0 font-weight-bold"
            >
              <template v-if="header.value === 'customer_name'">
                Summary:
              </template>
              <template v-if="header.value === 'total_amount'">
                {{ moneyNoSign(totalAmountSum / 100) }}
              </template>
              <template v-if="header.value === 'fee_amount'">
                {{ moneyNoSign(totalFeeSum / 100) }}
              </template>
              <template v-if="header.value === 'net_amount' || header.value === 'less_fee_amount'">
                {{ moneyNoSign((totalAmountSum - totalFeeSum) / 100) }}
              </template>
            </td>
            <td></td>
          </tr>
        </template>

        <template slot="no-data">
          No results found
        </template>
      </v-data-table>
    </div>

    <v-layout row wrap pr-1>
      <v-btn icon flat v-if="!showFooter" @click="showFooter=true"><v-icon>fa-angle-down</v-icon></v-btn>
      <v-btn icon flat v-if="showFooter" @click="showFooter=false"><v-icon>fa-angle-up</v-icon></v-btn>
      <v-spacer></v-spacer>
      <v-btn color="primary" @click="searchModal = true">
        Search
        <v-divider vertical class="ml-2 mr-2" v-if="hasPermission('has_inline_help')"></v-divider>
        <Lightbulb color="primary--text">
          <template v-slot:message>
            <span>Use the fields or buttons below to search specific transactions or run reports</span>
          </template>
        </Lightbulb>
      </v-btn>
      <v-btn @click="exportFull">Export</v-btn>
      <v-btn v-if="hasPermission('can_customize_columns')" @click="configModal = true">Config</v-btn>
    </v-layout>

    <v-dialog scrollable v-model="searchModal" @submit.prevent @keydown.esc.prevent="searchModal=false">
      <v-card>
        <v-toolbar dense flat>
          <v-toolbar-title>
            Search {{ paymentType === 'ach' ? 'ACH' : 'Cards' }}
            <Lightbulb medium>
              <template v-slot:message>
                <span>This screen will allow you to search for transactions using the custom search button. The Option button next to any transaction allows you to Refund, Reprint/Re-Email a receipt, recharge a customer and transactions details.</span>
              </template>
            </Lightbulb>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon flat color="primary" @click.native="searchModal = false">
            <v-icon>fa-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider class="primary"></v-divider>

        <v-card-text fill-height>
          <v-form @keyup.native.enter="searchNew">
            <v-container fluid pa-0>
              <v-layout row wrap>
                <v-flex xs6 sm3>
                  <v-text-field
                    :label="(paymentType === 'ach') ? 'Account Holder Name' : 'Card Holder Name'"
                    v-model="filter.customer_name"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Last Four (####)"
                    v-model="filter.last_four"
                    box
                    hide-details
                    mask="####"
                    type="tel"
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Amount"
                    v-model="filter.amount"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Transaction ID"
                    v-model="filter.tracking_id"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-menu
                    ref="startDateModal"
                    :close-on-content-click="false"
                    v-model="startDateModal"
                    :nudge-right="40"
                    :return-value.sync="filter.start_date"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <v-text-field
                      slot="activator"
                      v-model="formattedStartDate"
                      label="Created From"
                      append-icon="event"
                      box
                      hide-details
                    ></v-text-field>
                    <v-date-picker
                      v-model="filter.start_date"
                      no-title
                      @input="$refs.startDateModal.save(filter.start_date)"
                    ></v-date-picker>
                  </v-menu>
                  <v-menu
                    ref="endDateModal"
                    :close-on-content-click="false"
                    v-model="endDateModal"
                    :nudge-right="40"
                    :return-value.sync="filter.end_date"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <v-text-field
                      slot="activator"
                      v-model="formattedEndDate"
                      label="Created To"
                      append-icon="event"
                      box
                      hide-details
                    ></v-text-field>
                    <v-date-picker
                      v-model="filter.end_date"
                      no-title
                      @input="$refs.endDateModal.save(filter.end_date)"
                    ></v-date-picker>
                  </v-menu>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-menu
                    ref="settledStartDateModal"
                    :close-on-content-click="false"
                    v-model="settledStartDateModal"
                    :nudge-right="40"
                    :return-value.sync="filter.settled_start_date"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <v-text-field
                      slot="activator"
                      v-model="formattedSettledStartDate"
                      label="Settled From"
                      append-icon="event"
                      box
                      hide-details
                    ></v-text-field>
                    <v-date-picker
                      v-model="filter.settled_start_date"
                      no-title
                      @input="$refs.settledStartDateModal.save(filter.settled_start_date)"
                    ></v-date-picker>
                  </v-menu>
                  <v-menu
                    ref="settledEndDateModal"
                    :close-on-content-click="false"
                    v-model="settledEndDateModal"
                    :nudge-right="40"
                    :return-value.sync="filter.settled_end_date"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <v-text-field
                      slot="activator"
                      v-model="formattedSettledEndDate"
                      label="Settled To"
                      append-icon="event"
                      box
                      hide-details
                    ></v-text-field>
                    <v-date-picker
                      v-model="filter.settled_end_date"
                      no-title
                      @input="$refs.settledEndDateModal.save(filter.settled_end_date)"
                    ></v-date-picker>
                  </v-menu>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-select
                    name="request_type"
                    label="Request Type"
                    v-model="filter.request_type"
                    :items="requestTypes"
                    multiple
                    box
                    hide-details
                  ></v-select>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-select
                    name="transaction_status"
                    label="Status"
                    v-model="filter.transaction_status"
                    :items="statuses"
                    multiple
                    box
                    hide-details
                  ></v-select>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-select
                    name="recurring"
                    label="Recurring"
                    v-model="filter.is_recurring"
                    :items="booleanSelect"
                    box
                    hide-details
                  ></v-select>
                </v-flex>
                <v-flex xs6 sm3 v-if="paymentType !== 'ach'">
                  <v-select
                    name="fee_waived"
                    label="Waived"
                    v-model="filter.is_fee_waived"
                    :items="booleanSelect"
                    box
                    hide-details
                  ></v-select>
                </v-flex>
                <v-flex xs6 sm3 v-if="hasPermission('can_use_hpp')">
                  <v-select
                    name="hosted_payment"
                    label="Hosted Payment"
                    v-model="filter.is_hosted_payment"
                    :items="booleanSelect"
                    box
                    hide-details
                  />
                </v-flex>
                <v-flex xs6 sm3 v-if="paymentType === 'ach'">
                  <v-select
                    name="ach_is_authorized"
                    label="Authorized"
                    v-model="filter.ach_is_authorized"
                    :items="booleanSelect"
                    box
                    hide-details
                  ></v-select>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Response Code"
                    v-model="filter.response_code"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Email"
                    v-model="filter.billing_email"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Customer PO"
                    v-model="filter.customer_po"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Custom 1"
                    v-model="filter.custom_field_1"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Custom 2"
                    v-model="filter.custom_field_2"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Custom 3"
                    v-model="filter.custom_field_3"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Notes"
                    v-model="filter.notes"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3 v-if="paymentType === 'ach' && terminalsACH.length > 1">
                  <v-autocomplete
                    name="terminal"
                    label="Terminal"
                    v-model="filter.terminal_id"
                    :items="terminals"
                    item-text="name"
                    item-value="terminal_id"
                    box
                    hide-details
                  ></v-autocomplete>
                </v-flex>
                <v-flex xs6 sm3 v-if="paymentType !== 'ach' && terminalsCard.length > 1">
                  <v-autocomplete
                    name="terminal"
                    label="Terminal"
                    v-model="filter.terminal_id"
                    :items="terminals"
                    item-text="name"
                    item-value="terminal_id"
                    box
                    hide-details
                  ></v-autocomplete>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-autocomplete
                    name="account"
                    label="Account"
                    v-model="filter.account_id"
                    :items="accounts"
                    item-text="name"
                    item-value="account_id"
                    box
                    hide-details
                    v-if="myProfile.accounts.length > 1"
                  ></v-autocomplete>
                </v-flex>
              </v-layout>

              <v-divider class="primary"></v-divider>

              <v-layout row wrap>
                <v-flex xs8>
                  <v-btn @click="searchToday">Today</v-btn>
                  <v-btn @click="searchYesterday">Yesterday</v-btn>
                  <v-btn @click="searchThisMonth">This Month</v-btn>
                  <v-btn @click="searchLastMonth">Last Month</v-btn>
                  <v-btn @click="searchYearToDate">Year to Date</v-btn>
                </v-flex>
                <v-flex xs8 v-if="environment !== 'production' && hasPermission('superuser')">
                  <v-btn>Pending non approved</v-btn>
                </v-flex>
                <v-spacer></v-spacer>
                <v-flex xs4>
                  <v-layout row wrap justify-end>
                    <v-checkbox
                      v-if="hideTransactionsToggleVisible"
                      v-model="hideTransactions"
                      color="primary"
                      label="Hide Invalid Transactions"
                    />
                    <v-btn color="primary" @click="searchNew">Search</v-btn>
                    <v-btn @click="reset">Clear</v-btn>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog scrollable v-model="configModal" @submit.prevent @keydown.esc.prevent="configModal=false">
      <v-card>
        <v-toolbar dense flat>
          <v-toolbar-title>Options</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon flat color="primary" @click.native="configModal = false">
            <v-icon>fa-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider class="primary"></v-divider>

        <v-card-text fill-height>
          <v-form @keyup.native.enter="saveConfig">
            <v-container fluid>
              <h3>Display Columns</h3>
              <v-layout row wrap>
                <v-flex v-for="(column) in columns" :key="column.value">
                  <v-switch color="primary" hide-details :label="column.text" :value="column.visible" v-model="columnsDisplayed"></v-switch>
                </v-flex>
              </v-layout>

              <v-divider class="primary"></v-divider>

              <h3>Export Columns</h3>
              <v-layout row wrap>
                <v-flex v-for="(column) in columns" :key="column.value">
                  <v-switch color="primary" hide-details :label="column.text" :value="column.export" v-model="columnsExported"></v-switch>
                </v-flex>
              </v-layout>

              <v-divider class="primary"></v-divider>

              <v-layout row wrap justify-end>
                <v-btn color="primary" @click="saveConfig">Save</v-btn>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <OptionsModal v-if="sharedTransaction" :transaction="sharedTransaction" ref="sharedOptionsModal"></OptionsModal>
  </v-container>
</template>

<script>
import Moment from 'moment'
import JsonExcel from 'vue-json-excel'

import Bus from '@/bus'

import Lightbulb from '@/components/help/Lightbulb'
import OptionsModal from '@/components/transactions/OptionsModal.vue'

import ApiMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import DisplayMixin from '@/mixins/display'
import ExportMixin from '@/mixins/export'
import StoreMixin from '@/store/mixin'

export default {
  components: {JsonExcel, Lightbulb, OptionsModal},

  mixins: [ApiMixin, AuthMixin, DisplayMixin, ExportMixin, StoreMixin],

  data: function () {
    return {
      booleanSelect: [
        {value: '*', text: 'All'},
        {value: '0', text: 'No'},
        {value: '1', text: 'Yes'}
      ],

      requestTypes: this.defaultRequestTypes(),
      statuses: this.defaultStatuses(),

      achAuthOptions: [
        {value: 'none', text: 'None'},
        {value: 'onfile', text: 'Authorization on file'},
        {value: 'printsign', text: 'Print and sign now'},
        {value: 'print', text: 'Print and sign later'},
        {value: 'email', text: 'Send authorization email'}
      ],

      achStatusOptions: {
        1: 'Pending ACH authorization',
        2: 'Batched for processing',
        3: 'Pending bank approval',
        4: 'Approved',
        5: 'Declined'
      },

      achStatuses: [
        {value: 1, text: 'Pending ACH authorization'},
        {value: 2, text: 'Batched for processing'},
        {value: 3, text: 'Pending bank approval'},
        {value: 4, text: 'Approved'},
        {value: 5, text: 'Declined'}
      ],

      columns: this.defaultColumns(),
      pagination: this.defaultPagination(),
      rows: [],
      rowsAll: [],
      totalRows: 0,
      totalAmountSum: 0,
      totalFeeSum: 0,
      pageCountOptions: [5, 10, 25, 50, 100],
      loading: true,
      showFooter: true,

      // transaction: {},

      searchModal: false,
      startDateModal: false,
      endDateModal: false,
      settledStartDateModal: false,
      settledEndDateModal: false,

      configModal: false,
      hideTransactions: true,
      hideTransactionsToggleVisible: false,
      filter: this.defaultFilter(),
      sharedTransaction: null
    }
  },

  created () {
    Bus.$on('chargeSuccessful', this.search)
    Bus.$on('optionsClose', this.search)
    Bus.$on('openTransaction', this.openTransaction)
  },

  beforeDestroy () {
    Bus.$off('chargeSuccessful')
    Bus.$off('optionsClose')
    Bus.$off('openTransaction')
  },

  mounted () {
    if (this.$route.query.id) {
      this.openSharedTransaction(this.$route.query.id)
    } else if (this.$route.query.tracking_id) {
      const trackingId = this.$route.query.tracking_id

      if (typeof trackingId === 'string' && /^[a-zA-Z0-9_-]+$/.test(trackingId) && trackingId.length <= 100) {
        this.filter.tracking_id = trackingId
        this.search(trackingId)
      } else {
        console.warn('Invalid tracking_id parameter:', trackingId)
        this.$store.commit('warning', 'Invalid tracking ID format')
      }
    }
  },

  destroyed () {
    this.$store.commit('clearErrors')
    this.$store.commit('clearInfo')
  },

  props: {
    paymentType: String
  },

  computed: {
    environment () {
      return process.env.NODE_ENV
    },

    accounts: function () {
      let accounts = [
        {account_id: '*', name: 'All'}
      ]
      return accounts.concat(this.myProfile.accounts)
    },
    terminals: function () {
      let terminals = [
        {terminal_id: '*', name: 'All'}
      ]
      if (this.paymentType === 'ach') {
        return terminals.concat(this.terminalsACH)
      } else {
        return terminals.concat(this.terminalsCard)
      }
    },

    columnsDisplayed: function () {
      return this.columns.filter(column => {
        if (column.value === 'ach_is_authorized' && this.paymentType === 'ach') {
          column.visible = true
        }

        if (column.value === 'card_expiration_yymm' && this.paymentType === 'ach') {
          column.visible = false
        }

        if (column.value === 'terminal_code' && this.paymentType === 'ach' && this.terminalsACH.length < 2) {
          column.visible = false
        }
        if (column.value === 'terminal_code' && this.paymentType !== 'ach' && this.terminalsCard.length < 2) {
          column.visible = false
        }

        if (column.value === 'account_name' && this.myProfile.accounts.length < 2) {
          column.visible = false
        }
        if (column.value === 'transactions_source_id' && this.hasPermission('can_use_hpp')) {
          column.visible = true
        }
        if (this.myProfile.custom && this.myProfile.custom.hasOwnProperty(this.paymentType === 'ach' ? 'transaction_ach_columns' : 'transaction_columns')) {
          if (this.myProfile.custom[this.paymentType === 'ach' ? 'transaction_ach_columns' : 'transaction_columns'].includes(column.value)) {
            column.visible = true
          } else {
            column.visible = false
          }
        }

        if (column.visible) {
          return true
        }

        return false
      }, this)
    },

    columnsExported: function () {
      return this.columns.filter(column => {
        if (column.value === 'ach_is_authorized' && this.paymentType === 'ach') {
          column.export = true
        }

        if (column.value === 'card_expiration_yymm' && this.paymentType === 'ach') {
          column.export = false
        }

        if (column.value === 'terminal_code' && this.paymentType === 'ach' && this.terminalsACH.length < 2) {
          column.export = false
        }
        if (column.value === 'terminal_code' && this.paymentType !== 'ach' && this.terminalsCard.length < 2) {
          column.export = false
        }

        if (column.value === 'account_name' && this.myProfile.accounts.length < 2) {
          column.export = false
        }
        if (column.value === 'transactions_source_id' && this.hasPermission('can_use_hpp')) {
          column.export = true
        }

        if (this.myProfile.custom && this.myProfile.custom.hasOwnProperty(this.paymentType === 'ach' ? 'transaction_ach_export_columns' : 'transaction_export_columns')) {
          if (this.myProfile.custom[this.paymentType === 'ach' ? 'transaction_ach_export_columns' : 'transaction_export_columns'].includes(column.value)) {
            column.export = true
          } else {
            column.export = false
          }
        }

        if (column.export) {
          return true
        }

        return false
      }, this)
    },

    columnList: function () {
      return this.columns.map(column => column.value)
    },

    pages: function () {
      if (this.pagination.rowsPerPage == null ||
        this.pagination.totalItems == null
      ) return 0

      return Math.ceil(this.totalRows / this.pagination.rowsPerPage)
    },

    formattedStartDate () {
      return this.filter.start_date ? Moment(this.filter.start_date).format('MMM-DD-YYYY') : ''
    },
    formattedEndDate () {
      return this.filter.end_date ? Moment(this.filter.end_date).format('MMM-DD-YYYY') : ''
    },
    formattedSettledStartDate () {
      return this.filter.settled_start_date ? Moment(this.filter.settled_start_date).format('MMM-DD-YYYY') : ''
    },
    formattedSettledEndDate () {
      return this.filter.settled_end_date ? Moment(this.filter.settled_end_date).format('MMM-DD-YYYY') : ''
    }
  },

  watch: {
    $route (to, from) {
      // Only reset if the route path changed, not just the query params
      // The 'id' query param is used by OptionsModal and shouldn't trigger a reset
      if (to.path !== from.path) {
        this.reset()
      }
    }
  },

  methods: {
    defaultRequestTypes () {
      if (this.paymentType === 'ach') {
        return [
          {value: 'ACH', text: 'Sales'},
          {value: 'ACH Void', text: 'Voids'},
          {value: 'ACH Credit', text: 'Refunds'}
        ]
      } else {
        return [
          {value: 'Sale', text: 'Sales'},
          {value: 'Void', text: 'Voids'},
          {value: 'Credit', text: 'Refunds'}
        ]
      }
    },

    // HIDDEN: Will uncomment if readding ability to toggle hidden transactions.
    // toggleHiddenTransactions () {
    //   console.log('hideTransactions: ', this.hideTransactions)
    //   const showHidden = !this.hideTransactions // toggle the current value of hideTransactions
    //   this.hideTransactions = showHidden
    //   this.search('', showHidden)
    // },

    defaultStatuses () {
      if (this.paymentType === 'ach') {
        return ['Approved', 'Settled', 'Declined', 'Authorization', 'Batched', 'Pending', 'Voided']
      } else {
        return ['Pending', 'Approved', 'Settled', 'Declined', 'Voided']
      }
    },

    defaultColumns () {
      return [
        {
          value: 'tracking_id',
          text: 'ID',
          sortable: false,
          visible: false,
          export: false
        },
        {
          value: 'reference_number',
          text: 'Reference ID',
          sortable: false,
          visible: false,
          export: false
        },
        {
          value: 'customer_name',
          text: (this.paymentType === 'ach') ? 'Acct. Name' : 'Card Name',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'billing_email',
          text: 'Cust. Email',
          sortable: false,
          visible: false,
          export: false
        },
        {
          value: 'request_type',
          text: 'Type',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'transactions_source_id',
          type: 'is_hosted_payment',
          text: 'Hosted Payment',
          sortable: true,
          visible: false,
          export: false
        },
        {
          value: 'transaction_status',
          text: 'Status',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'last_four',
          text: 'Last Four',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'card_expiration_yymm',
          text: 'Expiration',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'payment_type',
          text: 'Card Type',
          sortable: false,
          visible: false,
          export: false
        },
        {
          value: 'total_amount',
          text: 'Amount ($)',
          sortable: true,
          visible: true,
          export: true,
          type: 'money'
        },
        {
          value: 'net_amount',
          text: 'Net ($)',
          sortable: true,
          visible: false,
          export: false,
          type: 'money'
        },
        {
          value: 'less_fee_amount',
          text: 'Less Adj ($)',
          sortable: true,
          visible: false,
          export: false,
          type: 'money'
        },
        {
          value: 'fee_amount',
          text: 'Fee ($)',
          sortable: true,
          visible: false,
          export: false,
          type: 'money'
        },
        {
          value: 'is_card_present',
          text: 'Card Present',
          sortable: false,
          visible: false,
          export: false,
          type: 'boolean'
        },
        {
          value: 'is_recurring',
          text: 'Recurring',
          sortable: true,
          visible: true,
          export: true,
          type: 'boolean'
        },
        {
          value: 'is_fee_waived',
          text: 'Fee Waived',
          sortable: true,
          visible: false,
          export: false,
          type: 'boolean'
        },
        {
          value: 'ach_is_authorized',
          text: 'Authorized',
          sortable: true,
          visible: false,
          export: false,
          type: 'boolean'
        },
        {
          value: 'order_number',
          text: 'Order Number',
          sortable: false,
          visible: false,
          export: false
        },
        {
          value: 'customer_po',
          text: 'Customer PO',
          sortable: false,
          visible: false,
          export: true
        },
        {
          value: 'custom_field_1',
          text: 'Custom 1',
          sortable: false,
          visible: false,
          export: true
        },
        {
          value: 'custom_field_2',
          text: 'Custom 2',
          sortable: false,
          visible: false,
          export: true
        },
        {
          value: 'custom_field_3',
          text: 'Custom 3',
          sortable: false,
          visible: false,
          export: true
        },
        {
          value: 'account_name',
          text: 'Account',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'terminal_name',
          text: 'Terminal',
          sortable: true,
          visible: false,
          export: true
        },
        {
          value: 'terminal_code',
          text: 'Terminal',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'user_name',
          text: 'Created By',
          sortable: false,
          visible: false,
          export: false
        },
        {
          value: 'user_email',
          text: 'User Email',
          sortable: false,
          visible: false,
          export: false
        },
        {
          value: 'response_code',
          text: 'Response Code',
          sortable: false,
          visible: false,
          export: false
        },
        {
          value: 'response_text',
          text: 'Response Text',
          sortable: false,
          visible: false,
          export: false
        },
        {
          value: 'ach_rejected',
          text: 'Rejected',
          sortable: true,
          visible: false,
          export: false,
          type: 'datetime'
        },
        {
          value: 'created',
          text: 'Created',
          sortable: true,
          visible: true,
          export: true,
          type: 'datetime'
        },
        {
          value: 'settled',
          text: 'Settled',
          sortable: true,
          visible: true,
          export: true,
          type: 'datetime'
        }
      ]
    },

    defaultFilter () {
      let basefilter = {
        account_id: '*',
        amount: '',
        customer_name: '',
        start_date: '',
        end_date: '',
        settled_start_date: '',
        settled_end_date: '',
        is_recurring: '*',
        is_hosted_payment: '*',
        last_four: '',
        response_code: '',
        terminal_id: '*',
        transaction_id: '',
        tracking_id: '',
        billing_email: '',
        billing_postalcode: '',
        customer_po: '',
        custom_field_1: '',
        custom_field_2: '',
        custom_field_3: '',
        notes: ''
      }

      if (this.paymentType === 'ach') {
        return {
          ...basefilter,
          request_type: ['ACH', 'ACH Void', 'ACH Credit'],
          transaction_status: ['Approved', 'Settled', 'Declined', 'Authorization', 'Batched', 'Pending', 'Voided'],
          ach_is_authorized: '*',
          ach_status_id: ''
        }
      } else {
        return {
          ...basefilter,
          request_type: ['Sale', 'Void', 'Credit'],
          transaction_status: ['Pending', 'Approved', 'Settled', 'Declined', 'Voided'],
          is_fee_waived: '*'
        }
      }
    },

    defaultPagination () {
      return {
        sortBy: this.$store.state.paginationDefaults.sortBy,
        descending: this.$store.state.paginationDefaults.descending,
        rowsPerPage: this.$store.state.paginationDefaults.rowsPerPage,
        page: 1,
        totalItems: 0
      }
    },

    openTransaction (id) {
      this.reset(id)
    },

    async openSharedTransaction (transactionId) {
      try {
        Bus.$emit('openWaitModal')
        this.sharedTransaction = { id: transactionId }
        await this.$nextTick()
        if (this.$refs.sharedOptionsModal) {
          this.$refs.sharedOptionsModal.showModal('details')
        }
      } catch (err) {
        Bus.$emit('openModal', {
          status: 'error',
          message: err.message || 'Failed to load transaction details'
        })
      } finally {
        Bus.$emit('closeWaitModal')
      }
    },

    searchNew () {
      this.pagination.page = 1
      this.search()
    },

    searchToday () {
      this.filter.start_date = Moment().format('YYYY-MM-DD')
      this.filter.end_date = Moment().format('YYYY-MM-DD')
    },

    searchYesterday () {
      this.filter.settled_start_date = Moment().subtract(1, 'days').format('YYYY-MM-DD')
      this.filter.settled_end_date = Moment().subtract(1, 'days').format('YYYY-MM-DD')
    },

    searchThisMonth () {
      this.filter.settled_start_date = Moment().startOf('month').format('YYYY-MM-DD')
      this.filter.settled_end_date = Moment().endOf('month').format('YYYY-MM-DD')
    },

    searchLastMonth () {
      this.filter.settled_start_date = Moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD')
      this.filter.settled_end_date = Moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD')
    },

    searchYearToDate () {
      this.filter.settled_start_date = Moment().startOf('year').format('YYYY-MM-DD')
      this.filter.settled_end_date = Moment().endOf('month').format('YYYY-MM-DD')
    },

    async search (id) {
      this.loading = true
      try {
        let filter = { ...this.filter }

        if (filter.is_hosted_payment === '1') {
          if (filter.is_hosted_payment === '1') {
            filter.transactions_source_id = 6
            delete filter.is_hosted_payment
          }
        }

        if (filter.start_date) {
          filter.start_date = Moment(this.filter.start_date).utc().format('YYYY-MM-DD HH:mm:ss')
        }
        if (filter.end_date) {
          filter.end_date = Moment(Moment(this.filter.end_date).format('YYYY-MM-DD') + ' 23:59:59').utc().format('YYYY-MM-DD HH:mm:ss')
        }

        if (filter.settled_start_date) {
          filter.settled_start_date = Moment(this.filter.settled_start_date).utc().format('YYYY-MM-DD HH:mm:ss')
        }
        if (filter.settled_end_date) {
          filter.settled_end_date = Moment(Moment(this.filter.settled_end_date).format('YYYY-MM-DD') + ' 23:59:59').utc().format('YYYY-MM-DD HH:mm:ss')
        }

        let result = await this.api({
          method: 'get',
          url: '/transactions',
          params: {
            filter: filter,
            page: this.pagination.page - 1,
            perPage: this.pagination.rowsPerPage,
            orderBy: this.pagination.sortBy,
            orderDirection: this.pagination.descending ? 'desc' : 'asc',
            hideTransactions: this.hideTransactions
          }
        })
        this.rows = result.data.rows
        this.totalRows = result.data.count
        this.pagination.totalItems = result.data.count
        this.totalAmountSum = result.data.total_amount_sum
        this.totalFeeSum = result.data.fee_amount_sum
        // this.pagination.page = 1

        // @TODO id is being set to a mouse event on intial search... why?
        if (typeof id === 'string' && id) {
          this.$nextTick(() => {
            this.$refs['options-' + id].showModal('receipt')
          })
        }
      } catch (err) {
        if (err.response && err.response.status === 401) {
          this.$store.commit('error', new Error('Token no longer valid, please login again'))
        } else {
          Bus.$emit('openModal', {
            status: 'error',
            message: err
          })
        }
      } finally {
        this.searchModal = false
        this.loading = false
      }
    },

    async exportFull () {
      this.loading = true

      try {
        let filter = { ...this.filter }

        if (filter.is_hosted_payment === '1') {
          if (filter.is_hosted_payment === '1') {
            filter.transactions_source_id = 6
            delete filter.is_hosted_payment
          }
        }

        if (filter.start_date) {
          filter.start_date = Moment(this.filter.start_date).utc().format('YYYY-MM-DD HH:mm:ss')
        }
        if (filter.end_date) {
          filter.end_date = Moment(Moment(this.filter.end_date).format('YYYY-MM-DD') + ' 23:59:59').utc().format('YYYY-MM-DD HH:mm:ss')
        }

        if (filter.settled_start_date) {
          filter.settled_start_date = Moment(this.filter.settled_start_date).utc().format('YYYY-MM-DD HH:mm:ss')
        }
        if (filter.settled_end_date) {
          filter.settled_end_date = Moment(Moment(this.filter.settled_end_date).format('YYYY-MM-DD') + ' 23:59:59').utc().format('YYYY-MM-DD HH:mm:ss')
        }

        let result = await this.api({
          method: 'get',
          url: '/transactions',
          params: {
            filter: filter,
            perPage: '*',
            orderBy: this.pagination.sortBy,
            orderDirection: this.pagination.descending ? 'desc' : 'asc'
          }
        })

        let csvContent = 'data:text/csv;charset=utf-8,'

        csvContent += [
          this.columnsExported.map(column => { return '"' + column.text + '"' }).join(','),
          ...result.data.rows.map(row => {
            return this.columnsExported.map(column => {
              let value = row[column.value]

              if (column.value === 'net_amount') {
                value = row['total_amount'] - row['fee_amount']
              }
              if (column.value === 'less_fee_amount') {
                value = row['total_amount'] - row['fee_amount']
              }
              if (column.type === 'is_hosted_payment') {
                value = (value === 6) ? 'Yes' : 'No'
              }
              if (column.value === 'card_expiration_yymm') {
                value = this.expiration(value)
              }

              if (value === null) {
                value = ''
              }
              if (column.type === 'boolean') {
                value = (value === 0) ? 'No' : 'Yes'
              }
              if (column.type === 'datetime') {
                value = this.datetime(value)
              }
              if (column.type === 'money') {
                value = '$' + (value / 100).toFixed(2)
                if (row['request_type'] === 'credit' || row['request_type'] === 'void') {
                  value = '(' + value + ')'
                }
              }

              return '"' + ('' + value).replace(/"/, '""') + '"'
            }).join(',')
          }),
          this.columnsExported.map(column => {
            if (column.value === 'customer_name') {
              return '"Summary:"'
            }
            if (column.value === 'total_amount') {
              return `"$${(this.totalAmountSum / 100).toFixed(2)}"`
            }
            if (column.value === 'fee_amount') {
              return `"$${(this.totalFeeSum / 100).toFixed(2)}"`
            }
            if (column.value === 'net_amount') {
              return `"$${((this.totalAmountSum - this.totalFeeSum) / 100).toFixed(2)}"`
            }
            if (column.value === 'less_fee_amount') {
              return `"$${((this.totalAmountSum - this.totalFeeSum) / 100).toFixed(2)}"`
            }
            return '""'
          }).join(',')
        ]
          .join('\n')

        await this.downloadCSVData(csvContent)
      } catch (err) {
        // this.$store.commit('error', err)
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      } finally {
        this.loading = false
      }
    },

    reset (id = '') {
      this.requestTypes = this.defaultRequestTypes()
      this.statuses = this.defaultStatuses()
      this.columns = this.defaultColumns()
      this.filter = this.defaultFilter()
      this.pagination = this.defaultPagination()

      this.search(id)
    },

    changeSort (column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
      this.search()
    },

    saveConfig () {}
  }
}
</script>

<style lang="less">
  #footer {
    background: var(--v-primary-lighten1);
    color: white;
  }
</style>



// WEBPACK FOOTER //
// src/components/Transactions.vue